import React, { useRef } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Camera, Loader2, RefreshCw, Replace, Trash2, AlertCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_BYTES = 10 * 1024 * 1024;
const OK_EXT = /\.(jpe?g|png|webp|heic|heif)$/i;

const checkFile = (file) => {
  if (!(file.type.startsWith('image/') || OK_EXT.test(file.name))) return 'Formato non supportato. Usa JPG, PNG, WebP o HEIC.';
  if (!OK_EXT.test(file.name) && !/image\/(jpeg|png|webp|heic|heif)/.test(file.type)) return 'Formato non supportato. Usa JPG, PNG, WebP o HEIC.';
  if (file.size > MAX_BYTES) return 'La foto supera i 10 MB. Scegline una più leggera.';
  return null;
};

const PhotoTile = ({ p, idx, onRemove, onRetry, onReplace, onPreviewError }) => (
  <div className="relative w-24" data-testid={`upload-photo-${idx}`}>
    <div className={`w-24 h-24 rounded-lg overflow-hidden border ${p.status === 'error' ? 'border-destructive' : 'border-border'} bg-secondary flex items-center justify-center`}>
      {p.preview && !p.noPreview ? <img src={p.preview} alt="Anteprima" className={`w-full h-full object-cover ${p.status === 'uploading' ? 'opacity-50' : ''}`} onError={() => onPreviewError()} />
        : <div className="flex flex-col items-center gap-1 text-muted-foreground px-1 text-center"><Camera className="h-6 w-6" /><span className="text-[10px] leading-tight truncate max-w-[88px]">{p.file?.name || 'Foto'}</span></div>}
      {p.status === 'uploading' && <Loader2 className="absolute h-6 w-6 animate-spin text-accent" />}
    </div>
    {p.status === 'error' && <p className="text-[10px] text-destructive mt-1 leading-tight" data-testid={`upload-photo-error-${idx}`}>{p.error}</p>}
    <div className="flex gap-1 mt-1">
      {p.status === 'error' && p.retryable && (
        <Button type="button" variant="outline" size="sm" className="h-7 w-7 p-0" onClick={onRetry} data-testid={`upload-photo-retry-${idx}`}><RefreshCw className="h-3 w-3" /></Button>
      )}
      <Button type="button" variant="outline" size="sm" className="h-7 w-7 p-0" disabled={p.status === 'uploading'} onClick={onReplace} data-testid={`upload-photo-replace-${idx}`}><Replace className="h-3 w-3" /></Button>
      <Button type="button" variant="outline" size="sm" className="h-7 w-7 p-0 text-destructive" disabled={p.status === 'uploading'} onClick={onRemove} data-testid={`upload-photo-remove-${idx}`}><Trash2 className="h-3 w-3" /></Button>
    </div>
  </div>
);

export const ReferencePhotoUploader = ({ config, photos, setPhotos, getAuthHeaders }) => {
  const inputRef = useRef(null);
  const replaceKey = useRef(null);
  const max = config.max_photos || 3;

  const patch = (key, data) => setPhotos((prev) => prev.map((p) => (p.key === key ? { ...p, ...data } : p)));

  const upload = async (key, file) => {
    patch(key, { status: 'uploading', error: null });
    const fd = new FormData();
    fd.append('file', file);
    try {
      const r = await axios.post(`${API}/reference-photos`, fd, { headers: getAuthHeaders(), timeout: 60000 });
      patch(key, { status: 'done', id: r.data.id });
      axios.get(`${API}/reference-photos/${r.data.id}`, { headers: getAuthHeaders(), responseType: 'blob' })
        .then((res) => setPhotos((prev) => prev.map((p) => {
          if (p.key !== key) return p;
          if (p.preview) URL.revokeObjectURL(p.preview);
          return { ...p, preview: URL.createObjectURL(res.data), noPreview: false };
        })))
        .catch(() => {});
    } catch (e) {
      const status = e.response?.status;
      const detail = typeof e.response?.data?.detail === 'string' ? e.response.data.detail : 'Caricamento non riuscito. Controlla la connessione e riprova.';
      patch(key, { status: 'error', error: detail, retryable: !status || status >= 500 });
    }
  };

  const discardServer = (p) => {
    if (p.id) axios.delete(`${API}/reference-photos/${p.id}`, { headers: getAuthHeaders() }).catch(() => {});
    if (p.preview) URL.revokeObjectURL(p.preview);
  };

  const addFile = (file) => {
    const error = checkFile(file);
    const entry = { key: `${Date.now()}-${Math.random()}`, file, preview: URL.createObjectURL(file), status: error ? 'error' : 'uploading', error, retryable: false };
    if (replaceKey.current) {
      const old = photos.find((p) => p.key === replaceKey.current);
      if (old) discardServer(old);
      setPhotos((prev) => prev.map((p) => (p.key === replaceKey.current ? entry : p)));
      replaceKey.current = null;
    } else {
      setPhotos((prev) => [...prev, entry]);
    }
    if (!error) upload(entry.key, file);
  };

  const onPick = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    const free = replaceKey.current ? 1 : max - photos.length;
    files.slice(0, free).forEach(addFile);
  };

  const remove = (p) => { discardServer(p); setPhotos((prev) => prev.filter((x) => x.key !== p.key)); };

  return (
    <div className="space-y-2" data-testid="reference-photo-uploader">
      <p className="text-base font-semibold">{config.emoji ? `${config.emoji} ` : ''}{config.label}</p>
      {config.hint && <p className="text-xs text-muted-foreground">{config.hint}</p>}
      <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif" multiple={!replaceKey.current} className="hidden" onChange={onPick} data-testid="reference-photo-input" />
      <div className="flex flex-wrap gap-3">
        {photos.map((p, i) => (
          <PhotoTile key={p.key} p={p} idx={i} onRemove={() => remove(p)} onRetry={() => upload(p.key, p.file)}
            onPreviewError={() => patch(p.key, { noPreview: true })}
            onReplace={() => { replaceKey.current = p.key; inputRef.current?.click(); }} />
        ))}
        {photos.length < max && (
          <button type="button" onClick={() => { replaceKey.current = null; inputRef.current?.click(); }}
            className="w-24 h-24 rounded-lg border-2 border-dashed border-border hover:border-accent flex flex-col items-center justify-center gap-1 text-muted-foreground hover:text-accent transition-colors"
            data-testid="reference-photo-add-btn">
            <Camera className="h-6 w-6" /><span className="text-xs">Aggiungi</span>
          </button>
        )}
      </div>
      {photos.some((p) => p.status === 'error') && (
        <p className="text-xs text-muted-foreground flex items-center gap-1"><AlertCircle className="h-3 w-3" /> Le foto con errore non verranno inviate: puoi riprovare, sostituirle o proseguire senza.</p>
      )}
    </div>
  );
};

export default ReferencePhotoUploader;
